import { List, useTable } from "@refinedev/antd";
import { useGetIdentity, useList, useNotification } from "@refinedev/core";
import {
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { useState } from "react";

import { api } from "../api";
import type { Me } from "../session";
import type { PermissionInfo, Role } from "../types";

type RoleValues = { name: string; permissions: string[] };

/**
 * /team/roles: roles are named sets of permissions (permission `staff.manage`). Staff can only
 * give a role permissions they have themselves; the owner role cannot be changed.
 */
export function RolesPage() {
  const { data: me } = useGetIdentity<Me>();
  const { open } = useNotification();
  const { tableProps, tableQuery } = useTable<Role>({
    resource: "roles",
    pagination: { mode: "off" },
    syncWithLocation: false,
  });
  const { result: catalog } = useList<PermissionInfo>({
    resource: "permissions",
    pagination: { mode: "off" },
  });
  const labels = new Map(catalog.data.map((permission) => [permission.name, permission.label]));
  const groups = [...new Set(catalog.data.map((permission) => permission.group))];

  const [editing, setEditing] = useState<Role | "new" | null>(null);
  const [busy, setBusy] = useState(false);
  const [form] = Form.useForm<RoleValues>();

  async function run(action: () => Promise<unknown>, success: string): Promise<boolean> {
    setBusy(true);
    try {
      await action();
      open?.({ type: "success", message: success });
      await tableQuery.refetch();
      return true;
    } catch (error) {
      open?.({ type: "error", message: error instanceof Error ? error.message : "Hata oluştu." });
      return false;
    } finally {
      setBusy(false);
    }
  }

  function edit(role: Role | "new") {
    form.setFieldsValue(
      role === "new"
        ? { name: "", permissions: [] }
        : { name: role.name, permissions: role.permissions },
    );
    setEditing(role);
  }

  async function save(values: RoleValues) {
    const ok =
      editing === "new"
        ? await run(
            () => api("/api/office/roles", { method: "POST", body: values }),
            "Rol oluşturuldu.",
          )
        : editing
          ? await run(
              () => api(`/api/office/roles/${editing.id}`, { method: "PUT", body: values }),
              "Rol güncellendi.",
            )
          : false;
    if (ok) setEditing(null);
  }

  return (
    <List
      title="Roller"
      headerButtons={
        <Button type="primary" onClick={() => edit("new")}>
          Rol oluştur
        </Button>
      }
    >
      <Table {...tableProps} rowKey="id" scroll={{ x: "max-content" }}>
        <Table.Column<Role>
          title="Rol"
          dataIndex="name"
          render={(name) => <strong>{name}</strong>}
        />
        <Table.Column<Role>
          title="İzinler"
          dataIndex="permissions"
          render={(permissions: string[], role) =>
            role.isOwner ? (
              <Tag color="purple">Tüm izinler</Tag>
            ) : (
              permissions.map((permission) => (
                <Tag key={permission}>{labels.get(permission) ?? permission}</Tag>
              ))
            )
          }
        />
        <Table.Column<Role> title="Üye" dataIndex="holderCount" />
        <Table.Column<Role>
          title=""
          key="actions"
          render={(_, role) =>
            role.isOwner ? (
              <Typography.Text type="secondary">Değiştirilemez</Typography.Text>
            ) : (
              <Space>
                <Button size="small" onClick={() => edit(role)}>
                  Düzenle
                </Button>
                <Popconfirm
                  title="Rol silinsin mi?"
                  okText="Sil"
                  cancelText="Vazgeç"
                  onConfirm={() =>
                    run(
                      () => api(`/api/office/roles/${role.id}`, { method: "DELETE" }),
                      "Rol silindi.",
                    )
                  }
                >
                  <Button size="small" danger disabled={role.holderCount > 0}>
                    Sil
                  </Button>
                </Popconfirm>
              </Space>
            )
          }
        />
      </Table>

      <Modal
        title={editing === "new" ? "Rol oluştur" : "Rolü düzenle"}
        open={editing !== null}
        okText="Kaydet"
        cancelText="Vazgeç"
        confirmLoading={busy}
        onOk={() => form.submit()}
        onCancel={() => setEditing(null)}
      >
        <Form form={form} layout="vertical" requiredMark={false} onFinish={save}>
          <Form.Item
            label="Rol adı"
            name="name"
            rules={[{ required: true, min: 2, max: 60, message: "Rol adı 2–60 karakter olmalı." }]}
          >
            <Input placeholder="Örneğin: Eğitmen Onay" />
          </Form.Item>
          <Form.Item label="İzinler" name="permissions">
            <Checkbox.Group style={{ width: "100%" }}>
              <Space direction="vertical">
                {groups.map((group) => (
                  <Space key={group} direction="vertical" size={4}>
                    <Typography.Text type="secondary">{group}</Typography.Text>
                    {catalog.data
                      .filter((permission) => permission.group === group)
                      .map((permission) => (
                        <Checkbox
                          key={permission.name}
                          value={permission.name}
                          // Nobody gives a role more than they have (checked by the API too).
                          disabled={!me?.permissions.includes(permission.name)}
                        >
                          {permission.label}
                        </Checkbox>
                      ))}
                  </Space>
                ))}
              </Space>
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>
    </List>
  );
}
